import React, { useState, useEffect } from 'react';
import { documentAPI, investmentAPI } from '../../services/api';
import { Upload, Download, CheckSquare, Square, FileText, Edit, Plus, Trash2, X, Save, Check, FileDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useMobile } from '../../hooks/useMobile';
import ExcelExportModal from '../ExcelExportModal';

const DocumentsTab = ({ investmentId, investment }) => {
  const { user } = useAuth();
  const { canDelete = false, canEdit = false, canCreate = false, canDownload = false } = usePermissions() || {};
  const isMobile = useMobile();
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const [categories, setCategories] = useState([]);
  const [checklistItems, setChecklistItems] = useState({});
  const [investmentDocs, setInvestmentDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Edit states
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddItem, setShowAddItem] = useState(null);
  
  // Form states
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    remarks: '',
    third_party_vendor: false
  });

  useEffect(() => {
    initAndFetch();
  }, [investmentId]);

  const initAndFetch = async () => {
    // For legacy investments (created before per-investment doc cloning) materialize
    // the AMC template once. Idempotent on the backend - safe to call repeatedly.
    try {
      await documentAPI.materializeInvestmentTemplate(investmentId);
    } catch (e) {
      // Non-fatal: continue with whatever categories exist
      console.warn('Doc template materialize skipped:', e?.response?.data?.detail || e?.message);
    }
    await fetchData();
  };

  const fetchData = async () => {
    try {
      const [categoriesRes, docsRes] = await Promise.all([
        documentAPI.getCategories(investmentId),
        investmentAPI.getDocuments(investmentId),
      ]);

      setCategories(categoriesRes.data);
      setInvestmentDocs(docsRes.data);

      // Fetch checklist items for each category
      const itemsPromises = categoriesRes.data.map((cat) =>
        documentAPI.getItems(cat.id, investmentId).then((res) => ({ categoryId: cat.id, items: res.data }))
      );
      const itemsResults = await Promise.all(itemsPromises);

      const itemsMap = {};
      itemsResults.forEach(({ categoryId, items }) => {
        itemsMap[categoryId] = items;
      });
      setChecklistItems(itemsMap);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (checklistItemId, file) => {
    try {
      await investmentAPI.uploadDocument(investmentId, checklistItemId, file);
      fetchData();
      alert('Document uploaded successfully');
    } catch (error) {
      alert('Failed to upload document');
    }
  };

  const handleFileDownload = async (checklistItemId, fileName) => {
    try {
      const response = await investmentAPI.downloadDocument(investmentId, checklistItemId);
      
      // Backend now returns presigned URL
      if (response.data && response.data.download_url) {
        // Use presigned URL to download directly from S3
        const link = document.createElement('a');
        link.href = response.data.download_url;
        link.download = fileName || response.data.file_name;
        link.target = '_blank'; // Open in new tab if needed
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback for old blob response (shouldn't happen with S3)
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleMarkAvailable = async (checklistItemId, isAvailable) => {
    try {
      await investmentAPI.markDocument(investmentId, checklistItemId, {
        is_available: isAvailable,
      });
      fetchData();
    } catch (error) {
      alert('Failed to update document status');
    }
  };

  const handleDeleteDocument = async (checklistItemId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"? This will permanently delete the file from AWS S3.`)) {
      return;
    }
    
    try {
      await investmentAPI.deleteDocument(investmentId, checklistItemId);
      fetchData();
      alert('Document deleted successfully from AWS S3');
    } catch (error) {
      console.error('Failed to delete document:', error);
      alert('Failed to delete document. Please try again.');
    }
  };

  const getDocumentStatus = (checklistItemId) => {
    return investmentDocs.find((doc) => doc.checklist_item_id === checklistItemId);
  };

  const seedDocuments = async () => {
    if (!window.confirm('This will load default document categories. Continue?')) return;
    try {
      await documentAPI.seedDefaults();
      fetchData();
      alert('Default categories loaded');
    } catch (error) {
      alert('Failed to seed documents');
    }
  };

  // Category management
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      // New categories are investment-specific by default (is_standard=false)
      await documentAPI.createCategory({ 
        name: newCategoryName.trim(), 
        order: categories.length,
        is_standard: false,
        investment_id: investmentId
      });
      fetchData();
      setNewCategoryName('');
      setShowAddCategory(false);
    } catch (error) {
      alert('Failed to add category');
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    if (!editCategoryName.trim()) return;
    try {
      await documentAPI.updateCategory(categoryId, { name: editCategoryName.trim() });
      fetchData();
      setEditingCategory(null);
      setEditCategoryName('');
    } catch (error) {
      alert('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category and all its items?')) return;
    try {
      await documentAPI.deleteCategory(categoryId);
      fetchData();
    } catch (error) {
      alert('Failed to delete category');
    }
  };

  const startEditCategory = (category) => {
    setEditingCategory(category.id);
    setEditCategoryName(category.name);
  };

  const handleToggleCategoryStandard = async (category, newIsStandard) => {
    // If unchecking (making it investment-specific), show confirmation
    if (!newIsStandard) {
      const confirmed = window.confirm(
        'This will make this category specific to this investment only and remove it from all other investments. Are you sure?'
      );
      if (!confirmed) return;
    }

    try {
      const updateData = {
        is_standard: newIsStandard,
        investment_id: newIsStandard ? null : investmentId
      };
      await documentAPI.updateCategory(category.id, updateData);
      fetchData();
      
      if (newIsStandard) {
        alert('Category is now standard and will appear in all investments.');
      } else {
        alert('Category is now specific to this investment only.');
      }
    } catch (error) {
      console.error('Failed to toggle category standard:', error);
      alert(error.response?.data?.detail || 'Failed to update category');
    }
  };

  // Checklist item management
  const handleAddItem = async (categoryId) => {
    if (!itemForm.name.trim()) {
      alert('Document name is required');
      return;
    }
    try {
      // New documents added to a specific investment are investment-specific by default
      await documentAPI.createItem(categoryId, {
        ...itemForm,
        order: (checklistItems[categoryId]?.length || 0),
        is_standard: false,
        investment_id: investmentId
      });
      fetchData();
      resetItemForm();
      setShowAddItem(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add document');
    }
  };

  const handleUpdateItem = async (categoryId, itemId) => {
    if (!itemForm.name.trim()) {
      alert('Document name is required');
      return;
    }
    try {
      await documentAPI.updateItem(categoryId, itemId, itemForm);
      fetchData();
      resetItemForm();
      setEditingItem(null);
    } catch (error) {
      alert('Failed to update document');
    }
  };

  const handleDeleteItem = async (categoryId, itemId) => {
    if (!window.confirm('Delete this document from checklist?')) return;
    try {
      await documentAPI.deleteItem(categoryId, itemId);
      fetchData();
    } catch (error) {
      alert('Failed to delete document');
    }
  };

  const startEditItem = (item) => {
    setEditingItem(item.id);
    setItemForm({
      name: item.name,
      description: item.description || '',
      remarks: item.remarks || '',
      third_party_vendor: item.third_party_vendor
    });
  };

  const resetItemForm = () => {
    setItemForm({
      name: '',
      description: '',
      remarks: '',
      third_party_vendor: false
    });
    setEditingItem(null);
  };

  const handleToggleItemStandard = async (categoryId, item, newIsStandard) => {
    // If unchecking (making it investment-specific), show confirmation
    if (!newIsStandard) {
      const confirmed = window.confirm(
        'This will make this document specific to this investment only and remove it from all other investments. Are you sure?'
      );
      if (!confirmed) return;
    }

    try {
      const updateData = {
        is_standard: newIsStandard,
        investment_id: newIsStandard ? null : investmentId
      };
      await documentAPI.updateItem(categoryId, item.id, updateData);
      fetchData();
      
      if (newIsStandard) {
        alert('Document is now standard and will appear in all investments.');
      } else {
        alert('Document is now specific to this investment only.');
      }
    } catch (error) {
      console.error('Failed to toggle item standard:', error);
      alert(error.response?.data?.detail || 'Failed to update document');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (categories.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Document Categories</h3>
        <p className="text-gray-500 mb-6">Load default document checklist to get started</p>
        {canCreate && (
          <button
            onClick={seedDocuments}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Load Default Checklist
          </button>
        )}
      </div>
    );
  }

  // Prepare export data for documents
  const prepareExportData = () => {
    const exportData = [];
    categories.forEach(category => {
      const items = checklistItems[category.id] || [];
      items.forEach(item => {
        const doc = investmentDocs.find(d => d.checklist_item_id === item.id);
        exportData.push({
          Category: category.name,
          'Document Name': item.name,
          Description: item.description || '',
          Remarks: item.remarks || '',
          'Third Party Vendor': item.third_party_vendor ? 'Yes' : 'No',
          'File Uploaded': doc ? 'Yes' : 'No',
          'File Name': doc?.file_name || 'N/A'
        });
      });
    });
    return exportData;
  };

  const exportColumns = [
    { key: 'Category', label: 'Category', format: (val) => val },
    { key: 'Document Name', label: 'Document Name', format: (val) => val },
    { key: 'Description', label: 'Description', format: (val) => val },
    { key: 'Remarks', label: 'Remarks', format: (val) => val },
    { key: 'Third Party Vendor', label: 'Third Party Vendor', format: (val) => val },
    { key: 'File Uploaded', label: 'File Uploaded', format: (val) => val },
    { key: 'File Name', label: 'File Name', format: (val) => val },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Investment Documentation</h3>
        {!isMobile && (
          <div className="flex space-x-2">
            {canDownload && (
              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                title="Export to Excel (Admin and Fund Manager only)"
              >
                <FileDown size={18} className="mr-2" />
                Export Excel
              </button>
            )}
            {canCreate && (
              <>
                {categories.length === 0 && (
                  <button
                    onClick={seedDocuments}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Load Default Checklist
                  </button>
                )}
                <button
                  onClick={() => setShowAddCategory(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus size={18} className="mr-2" />
                  Add Category
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Category Form */}
      {showAddCategory && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold mb-3">Add New Category</h4>
          <div className="flex space-x-2">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Category name"
              className="flex-1 px-4 py-2 border rounded-lg"
              onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
            />
            <button
              onClick={handleAddCategory}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Save size={18} />
            </button>
            <button
              onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {categories.map((category) => (
          <div key={category.id} className="bg-white border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
              {editingCategory === category.id ? (
                <div className="flex items-center space-x-2 flex-1">
                  <input
                    type="text"
                    value={editCategoryName}
                    onChange={(e) => setEditCategoryName(e.target.value)}
                    className="flex-1 px-3 py-1 border rounded"
                    onKeyPress={(e) => e.key === 'Enter' && handleUpdateCategory(category.id)}
                    autoFocus
                  />
                  <button
                    onClick={() => handleUpdateCategory(category.id)}
                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                    title="Save"
                  >
                    <Check size={20} />
                  </button>
                  <button
                    onClick={() => { setEditingCategory(null); setEditCategoryName(''); }}
                    className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                    title="Cancel"
                  >
                    <X size={20} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4 flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-lg font-semibold text-gray-900">{category.name}</h4>
                    {canEdit && !isMobile && (
                      <button
                        onClick={() => startEditCategory(category)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit category name"
                      >
                        <Edit size={16} />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {(canCreate || canDelete) && !isMobile && (
                <div className="flex space-x-2">
                  {canCreate && (
                    <button
                      onClick={() => setShowAddItem(category.id)}
                      className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <Plus size={16} className="mr-1" />
                      Add Document
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Delete category (Admin only)"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Add/Edit Item Form */}
            {(showAddItem === category.id || (editingItem && checklistItems[category.id]?.find(i => i.id === editingItem))) && (
              <div className="p-6 bg-blue-50 border-b">
                <h5 className="font-semibold mb-3">{editingItem ? 'Edit Document' : 'Add New Document'}</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Document Name *</label>
                    <input
                      type="text"
                      value={itemForm.name}
                      onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="e.g., Term Sheet"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={itemForm.description}
                      onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      rows="2"
                      placeholder="Brief description of the document"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Remarks</label>
                    <input
                      type="text"
                      value={itemForm.remarks}
                      onChange={(e) => setItemForm({ ...itemForm, remarks: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Any additional remarks"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={itemForm.third_party_vendor}
                        onChange={(e) => setItemForm({ ...itemForm, third_party_vendor: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm">Third Party Vendor Document</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    onClick={() => { resetItemForm(); setShowAddItem(null); }}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => editingItem ? handleUpdateItem(category.id, editingItem) : handleAddItem(category.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingItem ? 'Update Document' : 'Add Document'}
                  </button>
                </div>
              </div>
            )}

            <div className="divide-y">
              {checklistItems[category.id]?.map((item) => {
                const docStatus = getDocumentStatus(item.id);
                const isEditing = editingItem === item.id;
                
                if (isEditing) return null; // Form is shown above
                
                return (
                  <div key={item.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          {canEdit ? (
                            <button
                              onClick={() =>
                                handleMarkAvailable(item.id, !docStatus?.is_available)
                              }
                              className="text-blue-600 hover:text-blue-700"
                            >
                              {docStatus?.is_available ? (
                                <CheckSquare size={20} />
                              ) : (
                                <Square size={20} />
                              )}
                            </button>
                          ) : docStatus?.is_available ? (
                            <CheckSquare size={20} className="text-green-600" />
                          ) : (
                            <Square size={20} className="text-gray-400" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-gray-900">{item.name}</p>
                              {canEdit && !isMobile && (
                                <button
                                  onClick={() => startEditItem(item)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Edit document"
                                >
                                  <Edit size={14} />
                                </button>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">Description:</span> {item.description}
                              </p>
                            )}
                            {item.remarks && (
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">Remarks:</span> {item.remarks}
                              </p>
                            )}
                            <div className="flex items-center space-x-2 mt-2">
                              {item.third_party_vendor && (
                                <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                  Third Party
                                </span>
                              )}
                              {docStatus?.file_name && (
                                <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  Uploaded: {docStatus.file_name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {/* Download button - Hidden on mobile */}
                        {docStatus?.file_name && canDownload && !isMobile && (
                          <button
                            onClick={() => handleFileDownload(item.id, docStatus.file_name)}
                            className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            title="Download document (Admin and Fund Manager only)"
                          >
                            <Download size={16} className="mr-1" />
                            Download
                          </button>
                        )}
                        
                        {/* Delete Uploaded File button - Hidden on mobile */}
                        {docStatus?.file_name && canCreate && !isMobile && (
                          <button
                            onClick={() => handleDeleteDocument(item.id, docStatus.file_name)}
                            className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            title="Delete uploaded file from AWS S3 (Admin and Fund Manager only)"
                          >
                            <Trash2 size={16} className="mr-1" />
                            Delete File
                          </button>
                        )}
                        
                        {/* Upload button - Hidden on mobile */}
                        {canCreate && !isMobile && (
                          <>
                            <label className="cursor-pointer flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                              <Upload size={16} className="mr-1" />
                              {docStatus?.file_name ? 'Replace' : 'Upload'}
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) handleFileUpload(item.id, file);
                                }}
                              />
                            </label>
                          </>
                        )}
                        
                        {/* Delete Checklist Item button - Hidden on mobile */}
                        {canDelete && !isMobile && (
                          <button
                            onClick={() => handleDeleteItem(category.id, item.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete checklist item (Admin only)"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {(!checklistItems[category.id] || checklistItems[category.id].length === 0) && (
                <div className="px-6 py-8 text-center text-gray-500">
                  No documents in this category. Click "Add Document" to add one.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <ExcelExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={prepareExportData()}
        columns={exportColumns}
        filename={`${investment?.portfolio_company_name || 'Investment'}_Documents`}
      />
    </div>
  );
};

export default DocumentsTab;
